import {
  stopIntercepting,
  tryLoadAndStartRecorder,
} from "@alwaysmeticulous/recorder-loader";
import { LDFlagSet } from "launchdarkly-js-client-sdk";
import * as z from "zod";

import { ORG_ID } from "../orgs";

import { meticulousBuild, meticulousEnabled } from "./data";

const MeticulousRecordingToken = "gogZYOiHQDH55k29QaSmVxq6CpuKPII01p0GuYPR";
let meticulousInitialized = false;

export const isMeticulousReplay = meticulousBuild;
export let isMeticulousRecording = false;

/**
 * Initializes Meticulous data recording
 * - the Meticulous network-recorder script is inserted by the server for RC
 * - the script immediately begins pre-collecting data, storing in memory, not sending over the network
 * - after user auth and flags loaded we check the org id
 * - if org id is in the allow-list, start the Meticulous recorder, which will send the pre-collected data and begin live recording
 * - if not in the allow-list, stop pre-collecting data
 */
export async function initMeticulous(flags: LDFlagSet) {
  // ensure no double-initialization
  if (meticulousInitialized) return;
  meticulousInitialized = true;

  // get allow-list of orgs from the feature flag
  const enabledForOrgs = parseAsStringArray(flags["meticulous-rc-orgs"]);
  const meticulousRecordingEnabled =
    meticulousEnabled && enabledForOrgs.includes(ORG_ID);

  if (meticulousRecordingEnabled) {
    isMeticulousRecording = true;

    // provide feature flag values to Meticulous context
    if (window.Meticulous) {
      Object.entries(flags).forEach(([key, value]) => {
        window.Meticulous?.context.recordFeatureFlag(
          key,
          valueToBooleanOrString(value),
        );
      });
    }

    // send pre-collected data and start recording/sending live data
    // if Meticulous is enabled for the environment, and if the org is in the allow-list
    await tryLoadAndStartRecorder({
      recordingToken: MeticulousRecordingToken,
      // false since we only record on RC and can accept the higher 20MB network data cap
      // https://app.meticulous.ai/docs/how-to/troubleshoot-recorder
      isProduction: false,
    });
  } else {
    // otherwise stop pre-collecting data
    await stopIntercepting();
  }
}

function parseAsStringArray(value: unknown): string[] {
  const result = z.array(z.string()).safeParse(value);
  return result.success ? result.data : [];
}

// Meticulous only supports boolean or string feature flag values
function valueToBooleanOrString(value: any) {
  if (typeof value === "boolean") {
    return value;
  } else if (value == null) {
    return String(value);
  } else if (typeof value === "object") {
    return JSON.stringify(value) ?? String(value);
  } else {
    return String(value);
  }
}
